<template>
  <div class="text-lg lg:text-xl grid-container" :class="paragraphClassList">
    <div
      class="col-span-4 sm:col-span-6 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3 p-9"
      :class="{
        'bg-lightBlueBg': !hasLink,
        'bg-lightGreen': hasLink,
      }"
    >
      <h2
        v-if="title"
        :id="id"
        v-blokkli-editable:field_title
        class="mb-4 text-xl md:text-3xl text-darkGreen"
      >
        {{ title }}
      </h2>
      <div
        v-if="text"
        v-blokkli-editable:field_text
        class="ck-content hyphens-auto mt-5 text-darkGreen"
        v-html="text"
      />
      <div v-if="linkItem" class="flex flex-col md:flex-row">
        <div v-for="(link, i) in normalizedLinkItems" :key="i" class="mt-5">
          <FieldItemTypeButton
            :link-title="link.linkTitle"
            :uri="link.uri"
            :index="i"
          />
        </div>
      </div>

      <BlokkliField :list="paragraphs" name="field_content" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { FieldListItemTyped } from '#blokkli-build/generated-types'
import type { FieldItemTypeLinkDescriptionFragment } from '#graphql-operations'
import { slugify } from '#vuepal/helpers/url'

// Define a custom type for the InfoBox component
interface InfoBoxProps {
  title: string
  text?: string
  linkItem?:
    | FieldItemTypeLinkDescriptionFragment[]
    | FieldItemTypeLinkDescriptionFragment
  paragraphs?: FieldListItemTyped[] | FieldListItemTyped | null
}

const props = defineProps<InfoBoxProps>()

const { options } = defineBlokkli({
  bundle: 'info_box',
  editor: {
    addBehaviour: 'no-form',
    editTitle: (el) => el.textContent,
    getDraggableElement: (el) => el.querySelector('.ck-content'),
    determineVisibleOptions: (ctx) => {
      if (!ctx.parentType) {
        return ['spacing', 'showInToc']
      }
      return []
    },
  },
  globalOptions: ['spacing', 'showInToc'],
  propsFieldMapping: {
    paragraphs: 'field_content',
  },
})

const normalizedLinkItems = computed(() => {
  // Ensure linkItem is always treated as an array
  return Array.isArray(props.linkItem)
    ? props.linkItem
    : props.linkItem
      ? [props.linkItem]
      : []
})

const hasLink = computed(() => {
  return normalizedLinkItems.value.length > 0
})

const id = computed(() => slugify(props.title || ''))

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacing === 'small') {
    classList.push('pt-6', 'lg:pt-10')
  } else if (options.value.spacing === 'large') {
    classList.push('pt-12', 'lg:pt-20')
  }

  return classList
})
</script>
